const IMAGE_TYPES_TO_COMPRESS = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const replaceFileExtension = (fileName, mimeType) => {
  const extension =
    mimeType === "image/webp"
      ? "webp"
      : mimeType === "image/png"
        ? "png"
        : "jpg";
  return `${fileName.replace(/\.[^.]+$/, "")}.${extension}`;
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read image."));
    };
    image.src = objectUrl;
  });

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Image compression failed.")),
      mimeType,
      quality,
    );
  });

export const compressImageFile = async (
  file,
  { maxSizeMB = 1.2, maxDimension = 1920 } = {},
) => {
  if (!file || !IMAGE_TYPES_TO_COMPRESS.has(file.type)) return file;

  try {
    const image = await loadImage(file);
    const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
    let width = Math.max(1, Math.round(image.width * scale));
    let height = Math.max(1, Math.round(image.height * scale));
    const outputType = file.type === "image/png" ? "image/webp" : file.type;
    const targetBytes = maxSizeMB * 1024 * 1024;
    let quality = 0.86;
    let compressedBlob;

    for (let attempt = 0; attempt < 6; attempt += 1) {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, width, height);
      compressedBlob = await canvasToBlob(canvas, outputType, quality);

      if (compressedBlob.size <= targetBytes) break;
      quality = Math.max(0.55, quality - 0.08);
      width = Math.max(1, Math.round(width * 0.88));
      height = Math.max(1, Math.round(height * 0.88));
    }

    if (!compressedBlob || compressedBlob.size >= file.size) return file;

    return new File(
      [compressedBlob],
      replaceFileExtension(file.name || "image", compressedBlob.type),
      {
        type: compressedBlob.type,
        lastModified: file.lastModified || Date.now(),
      },
    );
  } catch (error) {
    return file;
  }
};

export const compressImageFiles = async (files, options) =>
  Promise.all(
    Array.from(files || []).map((file) => compressImageFile(file, options)),
  );
